import React, { useState, useEffect, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Typography,
  Icon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonBase,
  Button,
} from '@material-ui/core';

import CircleChart from '../../../components/CircleChart';
import ActionsCourse from '../../../store/ducks/course';
import Lesson from './Lesson';
import Certificate from './Certificate';
import useStyles from './styles';
import Loading from './Loading';

const Course = props => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    id,
    name,
    instructorName,
    active,
    onActive,
    loading,
    conclusionPercent,
    releaseDate,
    showBlockedLessons,
    locked: courseLocked,
    studentProfileData,
  } = props;
  const nextLesson = useSelector(state => state.course.list[id]?.nextLesson);
  const [courseLoading, setCourseLoading] = useState(true);
  const [loadingNextLesson, setLoadingNextLesson] = useState(false);
  const [errorNextLesson, setErrorNextLesson] = useState(false);
  const [courseNext, setCourseNext] = useState(null);

  const loadNextItemCourse = useCallback(() => {
    if (!nextLesson && !loadingNextLesson) {
      dispatch(ActionsCourse.requestNextLesson(id));
      setLoadingNextLesson(true);
    }
  }, [dispatch, id, loadingNextLesson, nextLesson]);

  const handleRetryLoadNextLesson = useCallback(() => {
    if (nextLesson?.error && !loadingNextLesson) {
      dispatch(ActionsCourse.requestNextLesson(id));
      setLoadingNextLesson(true);
    }
  }, [dispatch, id, loadingNextLesson, nextLesson]);

  const handleExpand = useCallback(() => {
    onActive(id);
    loadNextItemCourse();
  }, [loadNextItemCourse, id, onActive]);

  useEffect(() => {
    setCourseLoading(!!loading);
  }, [loading]);

  useEffect(() => {
    if (nextLesson) {
      setLoadingNextLesson(false);
      if (nextLesson.error) {
        setErrorNextLesson(true);
        return;
      }
      const { certificate, daysToRelease, empty, completed } = nextLesson;
      const notShowBlockedLesson = daysToRelease > 0 && !showBlockedLessons;
      const notShowCertificate = !certificate || certificate?.daysToRelease > 0;

      if (empty || (notShowBlockedLesson && notShowCertificate)) {
        setCourseNext('EMPTY');
      } else if (!completed) {
        setCourseNext('NEXT_LESSON');
      } else if (notShowCertificate) {
        setCourseNext('LAST_LESSON');
      } else {
        setCourseNext('CERTIFICATE');
      }
    }
  }, [nextLesson, showBlockedLessons]);

  useEffect(() => {
    if (active && !nextLesson && !loadingNextLesson) {
      loadNextItemCourse();
    }
  }, [active, loadNextItemCourse, loadingNextLesson, nextLesson]);

  return courseLoading ? (
    <Loading />
  ) : (
    <Accordion
      className={classes.container}
      expanded={active}
      onChange={handleExpand}
      classes={{
        expanded: classes.containerExpanded,
        disabled: classes.containerDisabled,
      }}
      disabled={courseLocked}
    >
      <AccordionSummary
        className="course-header"
        classes={{
          content: classes.headerContent,
          expanded: classes.headerContentExpanded,
          disabled: classes.headerContentDisabled,
        }}
      >
        <ButtonBase className="course-header-button" component="div">
          {!courseLocked ? (
            <CircleChart
              className="course-header-chart"
              percentage={conclusionPercent || 0}
              size={48}
            />
          ) : (
            <Box className="course-header-chart-lock">
              <Icon>lock</Icon>
            </Box>
          )}
          <Box className="course-header-text-container">
            <Box className="course-header-text-container-start">
              <Typography
                className={`course-header-text-title ${
                  courseLocked ? 'lock' : ''
                }`}
                variant="subtitle2"
              >
                {name}
              </Typography>
              {!courseLocked ? (
                <Typography
                  className="course-header-text-teacher"
                  variant="caption"
                  component="i"
                >
                  {instructorName}
                </Typography>
              ) : (
                <Typography
                  className="course-header-text-lock"
                  variant="caption"
                  component="i"
                >
                  {`Disponível a partir de ${moment(releaseDate).format(
                    'DD/MM/YYYY',
                  )}`}
                </Typography>
              )}
            </Box>
            <Box className="course-header-text-container-end">
              <>
                <Typography
                  className={`course-header-text-expiration-date ${
                    courseLocked ? 'lock' : ''
                  }`}
                  variant="caption"
                >
                  {studentProfileData?.productAccessExpiration
                    ? `Expiração do acesso: ${studentProfileData?.productAccessExpiration}`
                    : 'Sem expiração de acesso.'}
                </Typography>
                {studentProfileData?.createdAt && (
                  <Typography
                    className={`course-header-text-register-date ${
                      courseLocked ? 'lock' : ''
                    }`}
                    variant="caption"
                  >
                    {`Data de cadastro: ${studentProfileData?.createdAt}`}
                  </Typography>
                )}
              </>
            </Box>
          </Box>

          {!courseLocked && (
            <Icon className="course-header-icon-expand">
              {`expand_${active ? 'less' : 'more'}`}
            </Icon>
          )}
        </ButtonBase>
      </AccordionSummary>
      {!courseLocked && (
        <AccordionDetails className="course-body">
          <Box className="course-body-content">
            <Typography
              className="course-body-list-title"
              variant="overline"
              component="label"
            >
              {loadingNextLesson && 'Carregando progresso...'}
              {courseNext === 'NEXT_LESSON' && 'Próxima aula:'}
              {courseNext === 'LAST_LESSON' && 'Última aula:'}
              {courseNext === 'CERTIFICATE' && 'Certificado:'}
            </Typography>
            {loadingNextLesson && (
              <Box className="course-body-list-content loading">
                <Lesson loading />
              </Box>
            )}
            {errorNextLesson && !loadingNextLesson && (
              <Box className="course-body-list-content next-lesson-error-container">
                <Icon className="next-lesson-error-icon">warning</Icon>
                <Box className="next-lesson-error-content">
                  <Typography
                    className="next-lesson-error-content-text"
                    variant="body2"
                  >
                    Não foi possível encontrar a próxima aula.
                  </Typography>
                  <Button
                    className="next-lesson-error-content-button"
                    onClick={handleRetryLoadNextLesson}
                  >
                    Tentar novamente
                  </Button>
                </Box>
              </Box>
            )}
            {courseNext === 'EMPTY' && (
              <Box className="course-body-list-content empty">
                <Lesson empty />
              </Box>
            )}
            {(courseNext === 'NEXT_LESSON' || courseNext === 'LAST_LESSON') && (
              <Box className="course-body-list-content next">
                <Lesson {...nextLesson} courseId={id} />
              </Box>
            )}
            {courseNext === 'CERTIFICATE' && (
              <Box className="course-body-list-content certificate">
                <Certificate courseId={id} />
              </Box>
            )}
          </Box>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default memo(Course);
